import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL
} from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { Residencial } from '../models/residencial.model';
import { Usuario } from '../models/usuario.model';

interface CrearResidencialPayload {
  residencial: {
    nombre: string;
    direccion: string;
    pais: string;
    cantidadResidentes: number;
    idioma: 'es' | 'en';
    zonaHoraria: string;
    moneda: string;
    estado: 'activa' | 'suspendida' | 'pendiente_pago';
  };
  admin: {
    nombre: string;
    correo: string;
    celular: string;
    permitirLogin: boolean;
  };
}

interface CrearResidencialResult {
  residencialId: string;
  adminUid: string;
}

@Injectable({ providedIn: 'root' })
export class ResidencialService {
  private firestore = inject(Firestore);
  private storage   = inject(Storage);
  private functions = inject(Functions);

  // ── Lectura — Superadmin ──────────────────────────────────────────────────

  // listener en tiempo real — usado por la lista de residenciales del panel superadmin
  getAll(): Observable<Residencial[]> {
    const col = collection(this.firestore, 'residenciales');
    return collectionData(col, { idField: 'id' }) as Observable<Residencial[]>;
  }

  // lectura puntual — usado en el CRUD de residenciales al abrir modo editar
  getById(id: string): Observable<Residencial> {
    const r = doc(this.firestore, `residenciales/${id}`);
    return docData(r, { idField: 'id' }) as Observable<Residencial>;
  }

  // lectura puntual del perfil del admin — getDoc garantiza datos frescos del servidor,
  // evitando que docData() emita primero desde la caché local con datos desactualizados
  async getAdmin(adminId: string): Promise<Usuario | undefined> {
    const snap = await getDoc(doc(this.firestore, `usuarios/${adminId}`));
    return snap.exists() ? { id: snap.id, ...snap.data() } as Usuario : undefined;
  }

  // ── Cloud Functions — Superadmin (escritura vía CF) ───────────────────────

  // crea residencial + Auth user del admin + custom claims + docs Firestore — usado en CRUD
  async crearConAdmin(payload: CrearResidencialPayload): Promise<CrearResidencialResult> {
    const fn     = httpsCallable<CrearResidencialPayload, CrearResidencialResult>(
      this.functions, 'crearResidencial'
    );
    const result = await fn(payload);
    return result.data;
  }

  // actualiza Auth + usuarios + admins del tenant — usado en CRUD (modo editar)
  async actualizarAdmin(adminId: string, data: { nombre: string; email: string; telefono: string; permitirLogin: boolean }): Promise<void> {
    const fn = httpsCallable<{ adminId: string; nombre: string; email: string; telefono: string; permitirLogin: boolean }, { actualizado: boolean }>(
      this.functions, 'actualizarResidencial'
    );
    await fn({ adminId, ...data });
  }

  // hard delete: elimina Auth, Firestore y Storage de toda la residencial — usado en CRUD (btn eliminar)
  async eliminar(residencialId: string): Promise<void> {
    const fn = httpsCallable<{ residencialId: string }, { eliminado: boolean }>(
      this.functions, 'eliminarResidencial'
    );
    await fn({ residencialId });
  }

  // ── Firestore directo — Superadmin (escritura) ────────────────────────────

  // actualización parcial directa — usado en CRUD para logo, cantidadGuardias y datos de la residencial
  async update(id: string, data: Partial<Omit<Residencial, 'id' | 'creadoEn'>>): Promise<void> {
    const r = doc(this.firestore, `residenciales/${id}`);
    await updateDoc(r, { ...data });
  }

  // ── Storage — Superadmin ──────────────────────────────────────────────────

  // sube el logo a Storage y devuelve su URL — usado en CRUD al guardar con logo nuevo
  async uploadLogo(residencialId: string, file: File): Promise<string> {
    const ext     = file.name.split('.').pop() ?? 'png';
    const logoRef = ref(this.storage, `residenciales/${residencialId}/logo.${ext}`);
    await uploadBytes(logoRef, file);
    return getDownloadURL(logoRef);
  }

  // ── Helpers — Superadmin (validación) ────────────────────────────────────

  // valida un correo contra usuarios — usado en CRUD antes de crear o al cambiar email del admin
  async emailAdminExiste(email: string, excludeId?: string): Promise<boolean> {
    const col  = collection(this.firestore, 'usuarios');
    const q    = query(col, where('email', '==', email));
    const snap = await getDocs(q);
    return snap.docs.some(d => d.id !== excludeId);
  }
}
